import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { cn } from '@workspace/ui/lib/utils'
import { useCallback, useEffect, useRef, useState } from 'react'

const CONFIRM_TIMEOUT_MS = 5000

interface ConfirmButtonProps extends Omit<React.ComponentProps<'button'>, 'onClick'> {
  onConfirm: () => void
  confirmLabel?: string
}

/**
 * A destructive action that asks once: the first click arms it, the second does it. It is the design
 * system's danger button, and turns solid red while it waits for the second click.
 */
function ConfirmButton({
  onConfirm,
  confirmLabel = 'Confirm',
  children,
  className,
  ...props
}: ConfirmButtonProps) {
  const [confirming, setConfirming] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null)

  const reset = useCallback(() => {
    setConfirming(false)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  const handleClick = () => {
    if (confirming) {
      reset()
      onConfirm()
    } else {
      setConfirming(true)
      timeoutRef.current = setTimeout(reset, CONFIRM_TIMEOUT_MS)
    }
  }

  return (
    <SecondaryButton
      tone="danger"
      size="sm"
      onClick={handleClick}
      className={cn(
        confirming && 'border-destructive bg-destructive text-white hover:bg-destructive/90',
        className
      )}
      {...props}
    >
      {confirming ? confirmLabel : children}
    </SecondaryButton>
  )
}

export { ConfirmButton }
